/**
 * Aggregate buckets — operational view.
 *
 * Shows the cross-execution state held by Aggregate nodes. The happy
 * path is `count_target` reached → status=emitted (done automatically
 * by the handler). The sweeper covers `timeout_at` reached. This
 * page is the manual escape hatch when something blocks both routes
 * (e.g. wrong count_target).
 *
 * Operator actions:
 *   - Flush: force-emit a collecting bucket regardless of count/timeout
 *   - Delete: throw a bucket away (used items lost; pre-emission only)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers, RefreshCw, Send, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  aggregateBucketsApi,
  type BucketSummary,
} from "@/lib/api-aggregate-buckets";
import { usePaginationState } from "@/lib/pagination";
import { usePermissions } from "@/lib/permissions";

type StatusFilter = "all" | BucketSummary["status"];

const STATUS_BADGE: Record<BucketSummary["status"], { tone: "active" | "danger" | "neutral" | "warning"; label: string }> = {
  collecting: { tone: "warning", label: "collecting" },
  emitted: { tone: "active", label: "emitted" },
  timed_out: { tone: "danger", label: "timed_out" },
  expired: { tone: "neutral", label: "expired" },
};

export function AggregateBucketsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canExecute = can("workflows", "execute");
  const qc = useQueryClient();
  const [status, setStatus] = React.useState<StatusFilter>("all");
  const { page, pageSize, setPage, setPageSize, reset } = usePaginationState();

  const query = useQuery({
    queryKey: ["aggregate-buckets", status, page, pageSize],
    queryFn: () =>
      aggregateBucketsApi.list({
        status: status === "all" ? undefined : status,
        page,
        page_size: pageSize,
      }),
    refetchInterval: 10_000,
  });

  const flushMutation = useMutation({
    mutationFn: (id: string) => aggregateBucketsApi.flush(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aggregate-buckets"] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => aggregateBucketsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aggregate-buckets"] }),
  });

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const isEmpty = !query.isLoading && rows.length === 0;

  return (
    <PageContainer>
      <PageHeader
        title={t("aggregateBuckets.title")}
        description={t("aggregateBuckets.subtitle")}
        actions={
          <Button size="sm" variant="outline" onClick={() => query.refetch()}>
            <RefreshCw className={"h-4 w-4 " + (query.isFetching ? "animate-spin" : "")} strokeWidth={1.5} />
            Refresh
          </Button>
        }
      />
      <PageContent>
        <div className="mb-3 flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Status</span>
          <select
            className="border border-border bg-background px-2 py-1"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StatusFilter);
              reset();
            }}
          >
            <option value="all">all</option>
            <option value="collecting">collecting</option>
            <option value="emitted">emitted</option>
            <option value="timed_out">timed_out</option>
            <option value="expired">expired</option>
          </select>
        </div>

        {isEmpty ? (
          <EmptyState
            icon={Layers}
            title={t("aggregateBuckets.noBuckets")}
            description={t("aggregateBuckets.noBucketsHint")}
          />
        ) : (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Workflow / node</Th>
                  <Th>Correlation key</Th>
                  <Th className="w-16 text-right">Count</Th>
                  <Th className="w-16 text-right">Target</Th>
                  <Th className="w-24">Status</Th>
                  <Th className="w-44">Timeout / Emitted</Th>
                  {canExecute && <Th className="w-44 text-right">Actions</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((b) => {
                  const badge = STATUS_BADGE[b.status];
                  const timeline =
                    b.emitted_at
                      ? "emitted " + new Date(b.emitted_at).toLocaleString()
                      : b.timeout_at
                        ? "timeout " + new Date(b.timeout_at).toLocaleString()
                        : "—";
                  return (
                    <Tr key={b.id}>
                      <Td>
                        <div className="font-medium">{b.workflow_name}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          node {b.node_id} · {b.aggregation}
                        </div>
                      </Td>
                      <Td className="font-mono text-xs">{b.correlation_key}</Td>
                      <Td className="text-right font-mono text-xs">{b.count}</Td>
                      <Td className="text-right font-mono text-xs">
                        {b.count_target ?? "—"}
                      </Td>
                      <Td>
                        <Badge tone={badge.tone}>{badge.label}</Badge>
                      </Td>
                      <Td className="font-mono text-xs text-muted-foreground">
                        {timeline}
                      </Td>
                      {canExecute && (
                        <Td className="text-right">
                          <div className="inline-flex items-center gap-1">
                            {b.status === "collecting" && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={flushMutation.isPending}
                                onClick={() => flushMutation.mutate(b.id)}
                                title="Force emit now"
                              >
                                <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                                Flush
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={deleteMutation.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Delete bucket for "${b.correlation_key}"? ` +
                                      `Collected items will be lost.`,
                                  )
                                ) {
                                  deleteMutation.mutate(b.id);
                                }
                              }}
                              title="Discard bucket"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </PageContent>
    </PageContainer>
  );
}
